import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Calendar as CalendarIcon, Clock, MapPin, ArrowLeft, CheckCircle2, BookOpen } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Calendar } from "@/components/ui/calendar";
import { format, addDays, isWeekend, isBefore, startOfDay } from "date-fns";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BookingPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const registrationId = searchParams.get('registration_id');
  
  const [registration, setRegistration] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [loading, setLoading] = useState(true);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (registrationId) {
      fetchRegistration();
    } else {
      toast.error('No registration found');
      navigate('/');
    }
  }, [registrationId]);

  useEffect(() => {
    if (selectedDate) {
      fetchAvailableSlots(format(selectedDate, 'yyyy-MM-dd'));
    }
  }, [selectedDate]);

  const fetchRegistration = async () => {
    try {
      const response = await axios.get(`${API}/registrations/${registrationId}`);
      setRegistration(response.data);
    } catch (error) {
      console.error(error);
      toast.error('Failed to load registration');
      navigate('/');
    } finally {
      setLoading(false);
    }
  };

  const fetchAvailableSlots = async (date) => {
    setSlotsLoading(true);
    try {
      const response = await axios.get(`${API}/bookings/available-slots/${date}`);
      setAvailableSlots(response.data.slots || []);
      setSelectedSlot(null);
    } catch (error) {
      console.error(error);
      toast.error('Failed to load available slots');
    } finally {
      setSlotsLoading(false);
    }
  };

  const handleBooking = async () => {
    if (!selectedDate || !selectedSlot) {
      toast.error('Please select a date and time slot');
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/bookings`, {
        registration_id: registrationId,
        booking_date: format(selectedDate, 'yyyy-MM-dd'),
        booking_time: selectedSlot
      });
      
      toast.success('Booking request submitted!');
      navigate(`/success?registration_id=${registrationId}&type=booking`);
    } catch (error) {
      console.error(error);
      toast.error(error.response?.data?.detail || 'Failed to create booking');
    } finally {
      setSubmitting(false);
    }
  };

  const formatTimeSlot = (slot) => {
    const [hours, minutes] = slot.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour > 12 ? hour - 12 : hour === 0 ? 12 : hour;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  const disabledDays = (date) => {
    return isBefore(date, startOfDay(new Date()));
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC]">
        <div className="animate-pulse text-slate-500">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      {/* Header */}
      <header className="glass-header sticky top-0 z-30 py-4 px-6 md:px-12">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-2 cursor-pointer" onClick={() => navigate('/')}>
            <div className="w-10 h-10 bg-[#1E3A8A] rounded-lg flex items-center justify-center">
              <BookOpen className="w-6 h-6 text-white" />
            </div>
            <div className="logo-text text-xl">
              English<span className="logo-accent">Test</span>Solution
            </div>
          </div>
        </div>
      </header>

      <main className="py-12 px-6 md:px-12">
        <div className="max-w-5xl mx-auto">
          <Button 
            variant="ghost" 
            className="mb-6 text-slate-600"
            onClick={() => navigate(`/success?registration_id=${registrationId}`)}
            data-testid="back-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-2" /> Back
          </Button>

          <div className="text-center mb-8">
            <h1 className="text-3xl md:text-4xl mb-4">Book Your Test Appointment</h1>
            <p className="text-lg text-slate-600">
              Select your preferred date and time for {registration?.test_name}
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {/* Calendar */}
            <Card className="shadow-lg">
              <CardHeader className="border-b">
                <CardTitle className="text-xl flex items-center gap-2">
                  <CalendarIcon className="w-5 h-5" />
                  Select Date
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <div className="flex justify-center">
                  <Calendar
                    mode="single"
                    selected={selectedDate}
                    onSelect={setSelectedDate}
                    disabled={disabledDays}
                    className="rounded-md border"
                    data-testid="booking-calendar"
                  />
                </div>
                <div className="mt-4 p-4 bg-slate-50 rounded-lg">
                  <p className="text-sm font-medium text-slate-700 mb-2">Test Center Hours:</p>
                  <div className="space-y-1 text-sm text-slate-600">
                    <p><strong>Weekdays:</strong> 2pm-4pm, 8pm-9pm</p>
                    <p><strong>Weekends:</strong> 8am-8pm</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Time Slots */}
            <Card className="shadow-lg">
              <CardHeader className="border-b">
                <CardTitle className="text-xl flex items-center gap-2">
                  <Clock className="w-5 h-5" />
                  Select Time
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                {!selectedDate ? (
                  <div className="text-center py-12 text-slate-500">
                    Please select a date first
                  </div>
                ) : slotsLoading ? (
                  <div className="text-center py-12 text-slate-500">
                    Loading available slots...
                  </div>
                ) : availableSlots.length === 0 ? (
                  <div className="text-center py-12 text-slate-500">
                    No available slots for this date
                  </div>
                ) : (
                  <div className="grid grid-cols-3 gap-3">
                    {availableSlots.map((slot) => (
                      <button
                        key={slot}
                        className={`calendar-slot ${selectedSlot === slot ? 'selected' : ''}`}
                        onClick={() => setSelectedSlot(slot)}
                        data-testid={`slot-${slot}`}
                      >
                        {formatTimeSlot(slot)}
                      </button>
                    ))}
                  </div>
                )}

                {selectedDate && selectedSlot && (
                  <div className="mt-6 p-4 bg-blue-50 border border-blue-100 rounded-lg">
                    <p className="font-medium text-slate-800">Selected Appointment:</p>
                    <p className="text-slate-600">
                      {format(selectedDate, 'EEEE, MMMM d, yyyy')} at {formatTimeSlot(selectedSlot)}
                    </p>
                  </div>
                )}

                <div className="mt-6 space-y-4">
                  <div className="flex items-start gap-2 text-sm text-slate-600">
                    <MapPin className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    <span>Test Centers: Langley & Surrey, BC, Canada</span>
                  </div>
                  <div className="bg-orange-50 border border-orange-200 rounded-lg p-3">
                    <p className="text-sm text-slate-700">
                      <strong>Note:</strong> Your booking will be reviewed and approved within 12 hours. 
                      You will receive a confirmation email once approved.
                    </p>
                  </div>
                </div>

                <Button
                  className="w-full btn-primary h-12 mt-6"
                  onClick={handleBooking}
                  disabled={!selectedDate || !selectedSlot || submitting}
                  data-testid="confirm-booking-btn"
                >
                  {submitting ? 'Submitting...' : 'Request Booking'}
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>
    </div>
  );
}
